/**
 * QuickAddSheet — the web's "+" sheet. Merges mobile's QuickScanSheet (photo /
 * gallery / voice) and AddFoodSheet (barcode / label / manual / saved foods).
 * Icon tiles follow mobile's AddFoodSheet: primary-tinted rounded square,
 * primary outline glyph — no per-option colors.
 */
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { AppIcon, type AppIconName } from '@/components/ui/AppIcon'
import { SectionLabel } from '@/components/ui/Card'
import type { MealTime } from '@/types/diary'

interface Tile { id: string; icon: AppIconName; titleKey: string; subtitleKey: string; path: string }

const SCAN_TILES: Tile[] = [
  { id: 'photo', icon: 'camera', titleKey: 'quickScan.scanMeal', subtitleKey: 'diary.addFood.options.scan.subtitle', path: '/log?mode=photo' },
  { id: 'gallery', icon: 'image', titleKey: 'quickScan.fromPhotos', subtitleKey: 'quickScan.fromPhotosDesc', path: '/log?mode=photo&pick=1' },
  { id: 'voice', icon: 'mic', titleKey: 'diary.addFood.options.quickLog.title', subtitleKey: 'diary.addFood.options.quickLog.subtitle', path: '/log?mode=describe' },
  { id: 'barcode', icon: 'barcode', titleKey: 'diary.addFood.options.barcode.title', subtitleKey: 'diary.addFood.options.barcode.subtitle', path: '/log?mode=barcode' },
  { id: 'label', icon: 'document', titleKey: 'diary.addFood.options.label.title', subtitleKey: 'diary.addFood.options.label.subtitle', path: '/log?mode=label' },
]
const OTHER_TILES: Tile[] = [
  { id: 'savedFoods', icon: 'heart', titleKey: 'diary.addFood.options.savedFoods.title', subtitleKey: 'diary.addFood.options.savedFoods.subtitle', path: '/saved-foods' },
  { id: 'recipes', icon: 'chef-hat', titleKey: 'diary.addFood.options.recipes.title', subtitleKey: 'diary.addFood.options.recipes.subtitle', path: '/recipes' },
  { id: 'manual', icon: 'create', titleKey: 'diary.addFood.options.manual.title', subtitleKey: 'diary.addFood.options.manual.subtitle', path: '/diary?manual=1' },
]

export function QuickAddSheet({ open, onClose, forDate, mealType }: { open: boolean; onClose: () => void; forDate?: string; mealType?: MealTime }) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const go = (tile: Tile) => {
    onClose()
    const params = new URLSearchParams()
    if (forDate) params.set('diaryDate', forDate)
    if (mealType) params.set('mealType', mealType)
    const q = params.toString()
    navigate(q ? `${tile.path}${tile.path.includes('?') ? '&' : '?'}${q}` : tile.path)
  }

  const grid = (tiles: Tile[]) => (
    <div className="grid grid-cols-2 gap-sm sm:grid-cols-3">
      {tiles.map((tile) => (
        <button key={tile.id} onClick={() => go(tile)} className="flex min-h-[112px] flex-col items-start rounded-lg bg-surface-inner px-md py-md text-left transition-colors hover:bg-border-light" aria-label={`${t(tile.titleKey)}. ${t(tile.subtitleKey)}`}>
          <span className="mb-sm flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><AppIcon name={tile.icon} size={22} /></span>
          <span className="text-[15px] font-semibold text-text">{t(tile.titleKey)}</span>
          <span className="mt-0.5 text-caption text-text-secondary">{t(tile.subtitleKey)}</span>
        </button>
      ))}
    </div>
  )

  return (
    <Modal open={open} onClose={onClose} wide title={mealType ? t('diary.addFood.addTo', { meal: t(`diary.meals.${mealType}`) }) : t('diary.addFood.title')}>
      <div className="flex flex-col gap-sm">
        <SectionLabel>{t('quickScan.title')}</SectionLabel>
        {grid(SCAN_TILES)}
        <SectionLabel className="mt-sm">{t('tabs.addFood')}</SectionLabel>
        {grid(OTHER_TILES)}
      </div>
    </Modal>
  )
}
